import missionData from "../data/missions.json";

/** Metric a daily mission tracks (derived from finished-run facts). */
export type MissionMetric =
  | "play_games"
  | "merges"
  | "endless_score"
  | "reach_tier"
  | "max_chain"
  | "clear_stage"
  | "play_daily"
  | "drops";

export interface MissionDef {
  id: string;
  metric: MissionMetric;
  /** "sum" accumulates across runs; "max" keeps the best single-run value. */
  agg: "sum" | "max";
  target: number;
  /** Stardust granted on claim. */
  reward: number;
  icon: string;
}

/** What a finished run contributes toward missions. */
export interface MissionRunFacts {
  mode: "campaign" | "daily" | "endless";
  won: boolean;
  score: number;
  maxTier: number;
  maxChain: number;
  merges: number;
  drops: number;
}

export const MISSIONS: readonly MissionDef[] = missionData as MissionDef[];

/** How many missions are surfaced per day. */
export const DAILY_MISSION_COUNT = 5;

const BY_ID = new Map<string, MissionDef>(MISSIONS.map((m) => [m.id, m]));
export function getMission(id: string): MissionDef | undefined {
  return BY_ID.get(id);
}

/**
 * Deterministic daily mission set, seeded by the date so every device gets the
 * same five missions on a given day and the set rotates day to day.
 */
export function missionsForDate(dateKey: string): MissionDef[] {
  let h = 2166136261;
  for (const ch of dateKey) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  const rand = (): number => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const pool = [...MISSIONS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, Math.min(DAILY_MISSION_COUNT, pool.length));
}

/** Value a finished run contributes toward a given metric (0 = no contribution). */
export function missionContribution(metric: MissionMetric, f: MissionRunFacts): number {
  switch (metric) {
    case "play_games":
      return 1;
    case "merges":
      return f.merges;
    case "drops":
      return f.drops;
    case "endless_score":
      return f.mode === "endless" ? f.score : 0;
    case "reach_tier":
      return f.maxTier;
    case "max_chain":
      return f.maxChain;
    case "clear_stage":
      return f.mode === "campaign" && f.won ? 1 : 0;
    case "play_daily":
      return f.mode === "daily" ? 1 : 0;
    default:
      return 0;
  }
}

/** Daily login-reward stardust for a given consecutive-day streak. */
export function loginReward(streak: number): number {
  return Math.min(30 + Math.max(0, streak - 1) * 6, 70);
}

/**
 * Scale a mission target to the player's board width. Larger boards have more
 * material on screen, so volume metrics (merges/scores) are bumped up and the
 * peak metrics (tier/chain) get a small nudge. Count metrics (play_games,
 * clear_stage, play_daily) stay fixed — they don't get easier with wider boards.
 */
export function scaledTarget(def: MissionDef, cols: number): number {
  const c = Math.max(4, Math.min(6, Math.floor(cols) || 4));
  if (c === 4) return def.target;
  switch (def.metric) {
    case "merges":
    case "drops":
    case "endless_score":
      return Math.round(def.target * (c === 5 ? 1.25 : 1.5));
    case "reach_tier":
      // Add 1 tier per width beyond 4 (capped at 12 = cosmos).
      return Math.min(12, def.target + (c - 4));
    case "max_chain":
      return def.target + (c === 6 ? 1 : 0);
    default:
      return def.target;
  }
}
